/**
 * StripeAuthHandler - Handles auth restoration after Stripe checkout
 * 
 * Restores user authentication state after returning from Stripe checkout flow.
 */
import { useEffect, useState } from 'react';
import { useAuth } from '../../context/auth';

function StripeAuthHandler() {
  const { user, loading } = useAuth();
  const [isRestoring, setIsRestoring] = useState(false);
  
  useEffect(() => {
    // Skip if already logged in or still loading
    if (user || loading || isRestoring) {
      return;
    }
    
    const checkStripeAuthNeeded = async () => {
      try {
        // Check if auto-login is needed after returning from Stripe
        const autoLoginNeeded = localStorage.getItem('stripe_auto_login_needed') === 'true';
        
        if (autoLoginNeeded) {
          console.log('Auto-login needed after Stripe checkout');
          setIsRestoring(true);
          
          // Get saved credentials
          const email = localStorage.getItem('stripe_auth_email');
          const uid = localStorage.getItem('stripe_auth_uid');
          
          if (!email || !uid) {
            console.error('Missing saved credentials for auto-login');
            localStorage.removeItem('stripe_auto_login_needed');
            return;
          }
          
          console.log('Found credentials for auto-login:', { email, uid });
          
          // Get auth and FirebaseAuth modules
          const { auth } = await import('../../firebase/firebase');
          const { setPersistence, browserLocalPersistence } = await import('firebase/auth');
          
          // Set persistence to LOCAL
          await setPersistence(auth, browserLocalPersistence);
          
          // Check if already signed in
          if (auth.currentUser?.uid === uid) {
            console.log('Already signed in with correct user, refreshing token');
            await auth.currentUser.getIdToken(true);
          } else {
            console.log('Attempting auto sign-in for user:', email);
            
            // Attempt auto-login using Firebase SDK
            try {
              // We don't have the password, so we can only restore if the user is still in Firebase cache
              // Otherwise, we'll rely on the AuthContext to handle redirecting to login
              
              // If currentUser exists but is different, sign out first
              if (auth.currentUser && auth.currentUser.uid !== uid) {
                await auth.signOut();
              }
              
              // If we get here, we need to redirect to success page with session ID
              const sessionId = localStorage.getItem('stripe_session_id');
              if (sessionId) {
                console.log('Redirecting to success page with session ID:', sessionId);
                window.location.href = `/success?session_id=${sessionId}`;
              }
            } catch (loginError) {
              console.error('Auto login failed:', loginError);
              // This is expected in most cases since we don't have the password
              // User will need to log in manually
            }
          }
          
          // Clear the flag regardless of success
          localStorage.removeItem('stripe_auto_login_needed');
        }
      } catch (error) {
        console.error('Error in StripeAuthHandler:', error);
      } finally {
        setIsRestoring(false);
      }
    };
    
    checkStripeAuthNeeded();
  }, [user, loading, isRestoring]);
  
  return null; // This component doesn't render anything
}

export default StripeAuthHandler;

